/* eslint-disable no-case-declarations */
/* eslint-disable @typescript-eslint/no-explicit-any */
import { useMemo } from "react";
import { Col, Row } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { useLocation } from "react-router-dom";
import { getInteractionSelector } from "redux/memoizedSelectors";
import DataLoader from "shared/components/data-loader";
import { useItem } from "shared/dynamic-imports";
import { IconDispatcher } from "shared/Icons";
import { ActionPopOver } from "shared/popovers/ActionsPopOver";
import { Borders, Button } from "shared/ui-basics";
import { EMPTY_GUID } from "utils/helpers";
import { v4 as uuid } from "uuid";
import {
	createMultipleInteractions,
	linkGroups,
	linkInteraction,
	linkRecordAttributeAPI,
	linkTags,
	unlinkGroups,
	unlinkRecordAttributeAPI,
	unlinkTags,
	updateAttributeAPI,
	updateUserModification,
} from "../../../redux/index";
import { Label_ts, StyledCol } from "../cx-components.style";
import { columns, columnsOrder, PanelColumn } from "./columns";
import { getAllMapsParams } from "features/comments/helpers";

type Props = {
	disabled: boolean;
	recordType: "cem_cxopportunity" | "cem_cxactiontask" | "cem_cxsolution";
	stageId?: string;
};

export default function DetailPanel({ disabled, recordType, stageId }: Props): JSX.Element {
	const { cjmIdStr, personaIdStr } = getAllMapsParams(useLocation().search) || {};

	const { t } = useTranslation();
	const { currentItem, updateAttribute } = useItem();
	const { displayedAttribute, hiddenAttribute, updateShowAttribute } = useColumns();
	const userName = useSelector((state: any) => state?.auth?.userInfo?.UserFullName);
	const interactions = useSelector(getInteractionSelector(personaIdStr, stageId));

	const handleLinkChange = ({ id, attributeName, single, attributeId, mode, entityType }: any) => {
		if (single) {
			const idUpdate = mode === "add" ? id : EMPTY_GUID;
			updateAttribute(attributeId, attributeName, idUpdate);
			const modfifiedCxAttribute = currentItem?.attributes.find((att: any) => att.id === attributeId);
			updateAttributeAPI({ attributeId, modifiedAttribNames: [attributeName], modfifiedCxAttribute: { ...modfifiedCxAttribute, [attributeName]: idUpdate } });
			return;
		}

		const att = currentItem?.attributes.find((att: any) => att.id === attributeId);
		const updatedLinks = mode === "add" ? [...(att?.[attributeName] || []), id] : att?.[attributeName]?.filter((item: any) => item !== id);
		let linkorUnlink;
		switch (attributeName) {
			case "tags":
				linkorUnlink = mode === "add" ? linkTags : unlinkTags;
				linkorUnlink({ tagIds: [id], recordIds: [attributeId], recordType: "cem_cxattribute" });
				updateUserModification({ objId: currentItem.id, objName: recordType, userName });

				updateAttribute(attributeId, attributeName, updatedLinks);

				break;
			case "groups":
				linkorUnlink = mode === "add" ? linkGroups : unlinkGroups;
				linkorUnlink({ groupIds: [id], recordIds: [attributeId], recordType: "cem_cxattribute" });
				updateUserModification({ objId: currentItem.id, objName: recordType, userName });
				updateAttribute(attributeId, attributeName, updatedLinks);
				break;
			case "linkedTouchpoints":
				linkorUnlink = mode === "add" ? linkRecordAttributeAPI : unlinkRecordAttributeAPI;
				updateAttribute(attributeId, attributeName, updatedLinks);
				linkorUnlink({ recEntityName: entityType, recIds: [id], attributeId, objId: currentItem.id, objName: recordType });
				const interaction = interactions?.filter((it: any) => it.channelId === id);
				if (mode === "add") {
					let relatedAttributeInteraction;
					switch (recordType) {
						case "cem_cxopportunity":
							relatedAttributeInteraction = "linkedOpps";
							break;
						case "cem_cxsolution":
							relatedAttributeInteraction = "linkedSols";
							break;
						case "cem_cxactiontask":
							relatedAttributeInteraction = "linkedActions";
							break;
					}
					if (interaction && interaction?.length > 0) {
						interaction?.forEach((inter: any) => {
						
							linkInteraction({
								recIds: [currentItem?.id] as any,
								stageId,
								itemId: inter.id,
								attribute: relatedAttributeInteraction,
								recEntityName: recordType,
							});
						});
					} else {
						//create persona interaction
						const newGuid = uuid();
						createMultipleInteractions({ personaInteractionsIds: { [id]: newGuid }, personaIdStr, cjmIdStr, ownerId: EMPTY_GUID, stageId }as any);

						linkInteraction({
							recIds: [currentItem.id]as any,
							stageId: stageId,
							itemId: newGuid,
							attribute: relatedAttributeInteraction,
							recEntityName: recordType,
						});
					}
				}

				break;
			default:
				linkorUnlink = mode === "add" ? linkRecordAttributeAPI : unlinkRecordAttributeAPI;
				updateAttribute(attributeId, attributeName, updatedLinks);
				linkorUnlink({ recEntityName: entityType, recIds: [id], attributeId, objId: currentItem.id, objName: recordType });
				break;
		}
	};

	const sortedOrder = (a: any, b: any) => columnsOrder.indexOf(a.type) - columnsOrder.indexOf(b.type)

	return (
		<div className="py-4">
			{displayedAttribute.sort(sortedOrder).map((attribute: any) => {
				const { id, name, type, isCustom }: any = attribute;
				const column: PanelColumn = columns?.find((column: PanelColumn) => column.type === type) as PanelColumn;
				const { icon, Component, componentProps }: PanelColumn = (column as PanelColumn) || {};
				const showHideButton = isCustom && !disabled;
				return (
					<Row key={id} style={{ minHeight: "50px" }}>
						<StyledCol xs={4} className="p-2 ps-5">
							<div>{IconDispatcher(icon)}</div>
							<Label_ts size={14} className="ps-2">
								{t(name)}
							</Label_ts>
							{showHideButton && (
								<Button variant="light-link" size="s" title={t("HIDE")} id="eyeButton" onClick={() => updateShowAttribute(id, false)}>
									<div> {IconDispatcher("HIDE_EYE")}</div>
								</Button>
							)}
						</StyledCol>
						<Col xs={8}>
							<DataLoader {...componentProps} disabledSpinner={true}>
								{Component && (
									<Component
										{...componentProps}
										recordId={currentItem.id}
										object={attribute}
										link={(id: string) => handleLinkChange({ id, mode: "add", attributeId: attribute.id, ...componentProps })}
										unlink={(id: string) => handleLinkChange({ id, mode: "remove", attributeId: attribute.id, ...componentProps })}
										isSwitchDisabled={disabled}
										disabled={disabled}
										attribute={attribute}
										recordType={recordType}
										stageId={stageId}
									/>
								)}
							</DataLoader>
						</Col>
					</Row>
				);
			})}
			{hiddenAttribute?.length > 0 && (
				<Borders top={!disabled} bottom={!disabled} className="w-50 p-1 my-3" key={hiddenAttribute?.length}>
					{!disabled && <ActionPopOver label="ADD_PROPERTY" variant="light-link" actions={hiddenAttribute} />}
				</Borders>
			)}
		</div>
	);
}

const useColumns = () => {
	const { t } = useTranslation();
	const { currentItem, setCurrentItem } = useItem();
	const displayedAttribute = currentItem?.attributes?.filter((attribute: any) => attribute?.isShown);
	const hiddenAttribute = useMemo(
		() =>
			currentItem?.attributes
				?.filter((attribute: any) => !attribute?.isShown)
				?.map((attribute: any) => {
					const icon = columns && columns.find((column: PanelColumn) => column.type === attribute?.type)?.icon;

					return {
						id: attribute?.id,
						text: t(attribute?.name),
						icon: icon,
						handleClick: () => updateShowAttribute(attribute?.id, true),
					};
				}),
		[currentItem?.attributes]
	);

	const updateShowAttribute = (attributeId: string, isShown: boolean) => {
		const modfifiedCxAttribute = currentItem?.attributes.find((att: any) => att.id === attributeId);
		const updatedModfifiedCxAttribute = { ...modfifiedCxAttribute, isShown: isShown };

		setCurrentItem((prev: any) => {
			return { ...prev, attributes: prev.attributes.map((attr: any) => (attr.id === attributeId ? updatedModfifiedCxAttribute : attr)) };
		});
		updateAttributeAPI({ attributeId, modifiedAttribNames: ["isshown"], modfifiedCxAttribute: updatedModfifiedCxAttribute });
	};

	return { displayedAttribute, hiddenAttribute, updateShowAttribute };
};
